import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { API_BASE } from "../config";
// ✅ IMPORT REMARK-GFM (Table ke liye zaroori hai)
import remarkGfm from 'remark-gfm';
import {
  Send, Plus, MessageSquare, Paperclip, X, Loader2, Menu, Trash2,
  Sun, Moon, FileText, Sparkles, MoreVertical, Copy, RefreshCw,
  StopCircle, ArrowDown, Check, AlertCircle, Image as ImageIcon
} from "lucide-react";
import ReactMarkdown from "react-markdown";

// --- CONFIGURATION ---
const API_BASE_URL = `${API_BASE}/AIChat`;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"];

// --- SUB-COMPONENT: TOAST NOTIFICATION 
const ToastContainer = ({ toasts, removeToast }) => (
  <div className="fixed top-5 right-5 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm animate-in slide-in-from-right fade-in duration-300 ${
          toast.type === "error" ? "bg-red-500" : "bg-green-500"
        }`}
      >
        {toast.type === "error" ? <AlertCircle size={16} /> : <Check size={16} />}
        {toast.message}
        <button onClick={() => removeToast(toast.id)} className="ml-2 opacity-80 hover:opacity-100">
          <X size={14} />
        </button>
      </div>
    ))}
  </div>
);

// --- SUB-COMPONENT: TYPEWRITER EFFECT (FIXED) ---
// ✅ FIX: Ab ye Markdown render karega, sirf text nahi
const Typewriter = ({ text, onComplete, remarkPlugins, components }) => {
  const [displayedText, setDisplayedText] = useState("");
  
  useEffect(() => {
    let index = 0;
    setDisplayedText(""); // Reset
    
    const intervalId = setInterval(() => {
      if (index < text.length - 1) {
        setDisplayedText((prev) => prev + text.charAt(index));
        index++;
      } else {
        setDisplayedText(text); // Ensure full text
        clearInterval(intervalId);
        if (onComplete) onComplete();
      }
    }, 5); // Fast typing speed

    return () => clearInterval(intervalId);
  }, [text, onComplete]);

  // ✅ CRITICAL FIX: ReactMarkdown ke through render karna
  return (
    <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
      {displayedText}
    </ReactMarkdown>
  );
};

// --- SUB-COMPONENT: MESSAGE ITEM (Rich Markdown & Memoized) ---
const MessageItem = React.memo(({ msg, isDarkMode, onCopy, onRegenerate, isLastBot }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy(msg.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const themeClasses = {
    botBubble: isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900 border border-gray-200',
    userBubble: 'bg-blue-600 text-white',
    codeBlock: isDarkMode ? 'bg-gray-950 border border-gray-700' : 'bg-gray-900 text-white border border-gray-800',
    inlineCode: isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-red-600',
    borderColor: isDarkMode ? 'border-gray-600' : 'border-gray-300',
    tableHeader: isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800',
    quoteBorder: isDarkMode ? 'border-blue-500 text-gray-300' : 'border-blue-600 text-gray-600'
  };

  // ✅ MEMOIZED COMPONENTS (Performance & Stability)
  const markdownComponents = useMemo(() => ({
    h1: ({node, ...props}) => <h1 className="text-2xl font-bold mt-4 mb-2 border-b pb-1 border-gray-500/30" {...props} />,
    h2: ({node, ...props}) => <h2 className="text-xl font-semibold mt-3 mb-2" {...props} />,
    h3: ({node, ...props}) => <h3 className="text-lg font-medium mt-2 mb-1" {...props} />,
    ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
    ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
    li: ({node, ...props}) => <li className="pl-1" {...props} />,
    blockquote: ({node, ...props}) => <blockquote className={`border-l-4 pl-4 italic my-3 ${themeClasses.quoteBorder}`} {...props} />,
    a: ({node, ...props}) => <a className="text-blue-400 hover:underline font-medium break-all" target="_blank" rel="noopener noreferrer" {...props} />,
    
    // 🔥 Table Styling
    table: ({node, ...props}) => (
      <div className={`overflow-x-auto my-4 rounded-lg border ${themeClasses.borderColor}`}>
        <table className="min-w-full divide-y divide-gray-600" {...props} />
      </div>
    ),
    thead: ({node, ...props}) => <thead className={`${themeClasses.tableHeader} uppercase text-xs font-semibold`} {...props} />,
    tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-600" {...props} />,
    tr: ({node, ...props}) => <tr className="hover:bg-white/5 transition-colors" {...props} />,
    th: ({node, ...props}) => <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap border-r border-gray-600 last:border-r-0" {...props} />,
    td: ({node, ...props}) => <td className="px-4 py-3 whitespace-nowrap text-sm border-r border-gray-600 last:border-r-0" {...props} />,

    code({ node, inline, className, children, ...props }) {
      return !inline ? (
        <div className={`rounded-lg p-3 my-3 overflow-x-auto ${themeClasses.codeBlock}`}>
          <code className="text-sm font-mono" {...props}>{children}</code>
        </div>
      ) : (
        <code className={`px-1.5 py-0.5 rounded ${themeClasses.inlineCode} font-mono text-xs font-bold`} {...props}>{children}</code>
      );
    }
  }), [isDarkMode, themeClasses]);

  const plugins = useMemo(() => [remarkGfm], []);

  return (
    <div className={`flex gap-4 ${msg.sender === "user" ? "justify-end" : "justify-start"} group`}>
      {msg.sender === "bot" && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shrink-0 mt-1 shadow-sm">
          <Sparkles size={16} className="text-white" />
        </div>
      )}

      <div className={`flex flex-col max-w-[95%] md:max-w-[85%]`}>
        <span className={`text-[10px] text-gray-400 mb-1 ${msg.sender === "user" ? "text-right" : "text-left"}`}>
          {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}
        </span>

        {msg.fileUrl && (
          <div className="mb-2">
            {msg.fileUrl.match(/\.(jpg|jpeg|png|webp)$/i) ? (
              <img src={`${API_BASE.replace('/api', '')}${msg.fileUrl}`} alt="Attachment" className="max-h-60 rounded-lg border shadow-sm object-cover" />
            ) : (
              <div className={`flex items-center gap-2 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}`}>
                <FileText size={18} className="text-blue-500" /> 
                <span className="text-xs truncate max-w-[200px]">Attachment</span>
              </div>
            )}
          </div>
        )}

        <div className={`relative px-5 py-3.5 rounded-2xl text-[15px] leading-7 shadow-sm overflow-hidden ${msg.sender === "user" ? `${themeClasses.userBubble} rounded-br-sm` : `${themeClasses.botBubble} rounded-bl-sm`}`}>
          {msg.sender === "bot" ? (
            <div className="markdown-content overflow-x-auto">
              {msg.animate ? (
                // ✅ PASS COMPONENTS & PLUGINS TO TYPEWRITER
                <Typewriter 
                  text={msg.text} 
                  remarkPlugins={plugins}
                  components={markdownComponents}
                />
              ) : (
                <ReactMarkdown
                  remarkPlugins={plugins} 
                  components={markdownComponents}
                >
                  {msg.text}
                </ReactMarkdown>
              )}
            </div>
          ) : (
            <span className="whitespace-pre-wrap">{msg.text}</span>
          )}

          {msg.sender === "bot" && !msg.animate && (
            <div className="absolute -bottom-6 left-0 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={handleCopy} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" title="Copy">
                {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
              </button>
              {isLastBot && (
                <button onClick={onRegenerate} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" title="Regenerate">
                  <RefreshCw size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {msg.sender === "user" && (
        <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center shrink-0 mt-1 shadow-sm">
          <span className="text-white text-xs font-bold">U</span>
        </div>
      )}
    </div>
  );
});

// --- MAIN COMPONENT ---
const AIChatPage = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const abortControllerRef = useRef(null);

  const token = sessionStorage.getItem("token");
  const loginID = sessionStorage.getItem("loginID") || "Guest";

  const api = axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}` }
  });

  const addToast = (message, type = "error") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => removeToast(id), 4000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  useEffect(() => {
    if (loginID) fetchSessions();
    const handleClickOutside = () => setActiveMenuId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [loginID]);

  useEffect(() => {
    if (currentSessionId) {
      fetchMessages(currentSessionId);
      setActiveMenuId(null);
    }
  }, [currentSessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) setIsDarkMode(savedTheme === 'dark');
  }, []);

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const handleScroll = () => {
    if (chatContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
      setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 300);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await api.get(`/sessions/${loginID}`);
      setSessions(res.data);
      if (res.data.length > 0 && !currentSessionId) {
        setCurrentSessionId(res.data[0].sessionId);
      }
    } catch (error) {
      console.error("Session load failed", error);
    }
  };

  const fetchMessages = async (sessionId) => {
    try {
      const res = await api.get(`/session/${sessionId}/messages`);
      const processed = res.data.map(m => ({ ...m, animate: false }));
      setMessages(processed);
    } catch (error) {
      addToast("Failed to load messages");
    }
  };

  const createNewSession = async () => {
    try {
      const res = await api.post(`/session`, { loginID });
      const newSession = res.data;
      setSessions([newSession, ...sessions]);
      setCurrentSessionId(newSession.sessionId);
      setMessages([]);
      if (window.innerWidth < 768) setIsSidebarOpen(false);
      setTimeout(() => textareaRef.current?.focus(), 100);
    } catch (error) {
      addToast("Could not create new chat");
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm("Delete this chat permanently?")) return;
    try {
      await api.delete(`/session/${sessionId}`);
      const updatedSessions = sessions.filter(s => s.sessionId !== sessionId);
      setSessions(updatedSessions);
      if (currentSessionId === sessionId) {
        if (updatedSessions.length > 0) setCurrentSessionId(updatedSessions[0].sessionId);
        else {
          setCurrentSessionId(null);
          setMessages([]);
        }
      }
      addToast("Chat deleted", "success");
    } catch (error) {
      addToast("Failed to delete chat");
    }
  };

  const stopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
      addToast("Generation stopped", "info");
    }
  };

  const handleSendMessage = async (e, overrideText = null) => {
    if (e) e.preventDefault();
    const textToSend = overrideText || input;

    if ((!textToSend.trim() && !file) || loading) return;

    if (file) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        addToast("Invalid file type.");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        addToast("File too large. Max size is 5MB.");
        return;
      }
    }

    let activeSessionId = currentSessionId;
    if (!activeSessionId) {
      try {
        const sessionRes = await api.post(`/session`, { loginID });
        const newSession = sessionRes.data;
        setSessions([newSession, ...sessions]);
        setCurrentSessionId(newSession.sessionId);
        activeSessionId = newSession.sessionId;
      } catch (error) {
        addToast("Connection failed");
        return;
      }
    }

    abortControllerRef.current = new AbortController();

    let fileBase64 = null;
    if (file) fileBase64 = await toBase64(file);

    const tempMsg = {
      sender: "user",
      text: textToSend,
      fileUrl: file ? URL.createObjectURL(file) : null,
      isTemp: true,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, tempMsg]);
    setInput("");
    setFile(null);
    setLoading(true);
    
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    if (fileInputRef.current) fileInputRef.current.value = "";

    try {
      const payload = {
        sessionId: activeSessionId,
        loginID: loginID,
        question: tempMsg.text,
        fileBase64: fileBase64,
        fileName: file ? file.name : null
      };

      const res = await api.post(`/message`, payload, {
        signal: abortControllerRef.current.signal
      });

      setMessages(prev => {
        const filtered = prev.filter(m => !m.isTemp);
        return [...filtered, 
          { 
            sender: "user", 
            text: payload.question, 
            fileUrl: res.data.fileUrl, 
            timestamp: new Date().toISOString(),
            animate: false 
          },
          { 
            sender: "bot", 
            text: res.data.reply, 
            animate: true,
            timestamp: new Date().toISOString() 
          }
        ];
      });

      if (res.data.updatedTitle) {
        setSessions(prev => prev.map(s => 
          s.sessionId === activeSessionId ? { ...s, title: res.data.updatedTitle } : s
        ));
      }

    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled");
      } else {
        setMessages(prev => [...prev, { sender: "bot", text: "⚠️ Error processing request.", animate: false }]);
        addToast(error.response?.data?.message || "Server Error");
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleCopy = useCallback((text) => {
    navigator.clipboard.writeText(text);
    addToast("Copied to clipboard", "success");
  }, []);

  const handleRegenerate = useCallback(() => {
    const lastUserMsg = [...messages].reverse().find(m => m.sender === "user");
    if (lastUserMsg) {
      handleSendMessage(null, lastUserMsg.text);
    }
  }, [messages]);

  const SUGGESTIONS = [
    { icon: <Sparkles size={16} />, text: "Analyze sales for last month" },
    { icon: <FileText size={16} />, text: "Summarize this medical report" },
    { icon: <MessageSquare size={16} />, text: "Draft a patient discharge summary" },
  ];

  const themeClasses = {
    bg: isDarkMode ? 'bg-gray-900' : 'bg-gray-50',
    text: isDarkMode ? 'text-gray-100' : 'text-gray-900',
    sidebar: isDarkMode ? 'bg-gray-950' : 'bg-white',
    sidebarBorder: isDarkMode ? 'border-gray-800' : 'border-gray-200',
    chatBg: isDarkMode ? 'bg-gray-900' : 'bg-white',
    inputBg: isDarkMode ? 'bg-gray-800' : 'bg-white',
    secondaryText: isDarkMode ? 'text-gray-400' : 'text-gray-600',
  };

  return (
    <div className={`flex h-screen ${themeClasses.bg} ${themeClasses.text} font-sans overflow-hidden transition-colors duration-300`}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className={`${isSidebarOpen ? "w-72" : "w-0 md:w-0"} ${themeClasses.sidebar} border-r ${themeClasses.sidebarBorder} flex flex-col transition-all duration-300 relative z-30`}>
        <button 
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className={`absolute -right-10 top-4 p-2 rounded-r-lg shadow-md border-y border-r ${themeClasses.sidebarBorder} ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
        >
          <Menu size={20} />
        </button>

        <div className={`p-4 ${!isSidebarOpen && "hidden"}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <Sparkles size={18} className="text-white" />
              </div>
              <span className="font-bold text-lg">HMS AI</span>
            </div>
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 rounded-lg hover:bg-gray-700/10">
              {isDarkMode ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} className="text-indigo-600" />}
            </button>
          </div>
          <button onClick={createNewSession} className={`w-full flex items-center justify-center gap-2 border rounded-lg p-3 transition-all font-medium shadow-sm ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
            <Plus size={18} /> New Chat
          </button>
        </div>

        <div className={`flex-1 overflow-y-auto px-2 py-2 ${!isSidebarOpen && "hidden"}`}>
          <div className={`text-xs font-bold ${themeClasses.secondaryText} px-3 mb-2 uppercase`}>Recent</div>
          <div className="space-y-1">
            {sessions.map((session) => (
              <div key={session.sessionId} className="relative group">
                <button
                  onClick={() => setCurrentSessionId(session.sessionId)}
                  className={`w-full text-left p-3 rounded-lg text-sm flex items-center gap-3 pr-8 transition-all ${currentSessionId === session.sessionId ? (isDarkMode ? 'bg-gray-800' : 'bg-gray-200') : 'hover:bg-gray-500/10'}`}
                >
                  <MessageSquare size={16} className={themeClasses.secondaryText} />
                  <span className="truncate flex-1">{session.title}</span>
                </button>
                
                {/* ✅ FIX: Made 3-dots ALWAYS visible (removed opacity classes) */}
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setActiveMenuId(activeMenuId === session.sessionId ? null : session.sessionId); 
                  }}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-gray-500/20 text-gray-400 hover:text-gray-600`}
                >
                  <MoreVertical size={16} />
                </button>

                {activeMenuId === session.sessionId && (
                  <div className={`absolute right-0 top-8 w-32 rounded-lg shadow-xl border z-50 overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <button 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        deleteSession(session.sessionId); 
                      }} 
                      className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-2"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`flex-1 flex flex-col h-full relative ${themeClasses.chatBg} transition-colors duration-300`}>
        <div 
          className="flex-1 overflow-y-auto p-4 md:p-8" 
          ref={chatContainerRef}
          onScroll={handleScroll}
        >
          <div className="max-w-4xl mx-auto space-y-6 pb-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center py-20 opacity-50 mt-20">
                <Sparkles size={48} className="mb-4 text-blue-500" />
                <h2 className="text-2xl font-bold mb-6">How can I help you today?</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-2xl px-4">
                  {SUGGESTIONS.map((s, i) => (
                    <button 
                      key={i} 
                      onClick={() => setInput(s.text)}
                      className={`p-4 rounded-xl border text-sm flex flex-col items-center gap-2 text-center transition-all hover:scale-105 ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                    >
                      {s.icon} {s.text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <MessageItem 
                  key={idx} 
                  msg={msg} 
                  isDarkMode={isDarkMode} 
                  onCopy={handleCopy}
                  onRegenerate={handleRegenerate}
                  isLastBot={idx === messages.length - 1 && msg.sender === 'bot'}
                />
              ))
            )}
            
            {loading && (
              <div className="flex gap-4 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-gray-600"></div>
                <div className={`px-5 py-4 rounded-2xl w-24 h-10 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
                  <div className="flex gap-1 justify-center items-center h-full">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {showScrollBtn && (
          <button 
            onClick={scrollToBottom}
            className="absolute bottom-28 right-8 p-2 rounded-full shadow-lg bg-blue-600 text-white hover:bg-blue-700 transition-all z-20"
          >
            <ArrowDown size={20} />
          </button>
        )}

        <div className={`p-4 border-t ${themeClasses.sidebarBorder}`}>
          <div className="max-w-4xl mx-auto">
            {file && (
              <div className={`mb-3 p-3 rounded-lg border flex items-center justify-between animate-in slide-in-from-bottom-2 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                 <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      {file.type.startsWith('image') ? <ImageIcon size={20} className="text-blue-600" /> : <FileText size={20} className="text-blue-600" />}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium truncate max-w-[200px]">{file.name}</span>
                      <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                 </div>
                 <button onClick={() => { setFile(null); if(fileInputRef.current) fileInputRef.current.value = ""; }} className="p-1 hover:bg-gray-500/10 rounded-full">
                    <X size={18} />
                 </button>
              </div>
            )}

            <div className={`flex items-end gap-2 p-2 rounded-xl border shadow-sm transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300 focus-within:ring-2 focus-within:ring-blue-100'}`}>
              <button 
                onClick={() => fileInputRef.current.click()}
                className={`p-2.5 rounded-lg transition-colors ${themeClasses.secondaryText} hover:bg-gray-500/10`}
                title="Attach file (Max 5MB)"
              >
                <Paperclip size={20} />
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                onChange={(e) => setFile(e.target.files[0])} 
                accept=".jpg,.jpeg,.png,.webp,.pdf,.xlsx" 
              />

              <textarea
                ref={textareaRef}
                className={`flex-1 bg-transparent border-none focus:ring-0 resize-none py-2.5 max-h-32 min-h-[44px] ${themeClasses.text} placeholder-gray-400`}
                placeholder="Ask HMS AI anything..."
                rows={1}
                value={input}
                onChange={(e) => { setInput(e.target.value); adjustTextareaHeight(); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />

              {loading ? (
                <button 
                  onClick={stopGeneration}
                  className="p-2.5 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-all"
                  title="Stop generating"
                >
                  <StopCircle size={20} />
                </button>
              ) : (
                <button 
                  onClick={() => handleSendMessage()}
                  disabled={!input.trim() && !file}
                  className={`p-2.5 rounded-lg transition-all ${(!input.trim() && !file) ? 'bg-gray-500/20 text-gray-400' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'}`}
                >
                  <Send size={20} />
                </button>
              )}
            </div>
            
            <div className="text-center mt-2 flex justify-between px-2">
              <span className="text-[10px] text-gray-400">AI can make mistakes. Verify important info.</span>
              <span className="text-[10px] text-gray-400">{input.length} chars</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatPage;